import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sensible defaults in case the settings endpoint is briefly unreachable —
// they match the historical anchor so first paint still looks correct.
const FALLBACK = {
  litres_per_day: 3000,
  baseline_litres: 0,
  baseline_at: "2024-12-15T00:00:00Z",
};

const formatLitres = (v) =>
  v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const WaterCounter = () => {
  const [settings, setSettings] = useState(FALLBACK);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    axios
      .get(`${API}/counter-settings`)
      .then((r) => setSettings(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Tick every 1s — spec: "counter increasing every 1 second".
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const value = useMemo(() => {
    const baselineMs = new Date(settings.baseline_at).getTime();
    const perMs = settings.litres_per_day / (24 * 60 * 60 * 1000);
    return Math.max(0, settings.baseline_litres + (now - baselineMs) * perMs);
  }, [settings, now]);

  return (
    <motion.section
      data-testid="water-counter-section"
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.1 }}
      className="relative bg-black text-[#F5F5F7] border-y border-white/10 pt-14 overflow-hidden"
      role="status"
      aria-live="polite"
    >
      {/* Ambient green wash — sets the "living, eco" tone without shouting. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          background:
            "radial-gradient(ellipse at 50% 60%, rgba(74,222,128,0.18) 0%, transparent 65%)",
        }}
      />

      {/* Slow shimmer sweep — one full pass every ~9 s. Draws the eye without
          feeling like a promo strip. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 w-1/3 -left-1/3 opacity-70 water-counter-shimmer"
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.06) 45%, rgba(74,222,128,0.10) 50%, rgba(255,255,255,0.06) 55%, transparent 100%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-4 md:py-5">
        <p className="text-center text-[13px] md:text-[15px] lg:text-base leading-snug text-[#F5F5F7]/90">
          Nyla is proud to have saved{" "}
          <span
            data-testid="water-counter-value"
            className="font-display font-bold tabular-nums text-white text-[15px] md:text-lg lg:text-xl align-baseline"
            style={{
              textShadow: "0 0 24px rgba(74,222,128,0.35)",
            }}
          >
            {formatLitres(value)}
          </span>{" "}
          <span className="text-[#F5F5F7]/80">litres of groundwater</span>
          <span className="mx-1.5 text-white/30">—</span>
          <span className="text-[#F5F5F7]/70">
            leaving the Earth's water where it belongs.
          </span>
        </p>
      </div>

      <style>{`
        @keyframes water-counter-shimmer-anim {
          0%   { transform: translateX(0); }
          100% { transform: translateX(400%); }
        }
        .water-counter-shimmer {
          animation: water-counter-shimmer-anim 9s linear infinite;
        }
      `}</style>
    </motion.section>
  );
};
